import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import { Button } from '@/app/common/components/Button'
import { Input } from '@/app/common/components/Input'
import { BackIcon, CartIcon, MagnifierIcon } from '@/app/common/icons/general'
import { cn, currencyFormatter } from '@/app/common/utilities'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SimpleSelector, { ListItem } from '@/common/components/SimpleSelector'
import { TableToolBar } from '@/common/components/StackedTable.tsx'
import { FilterIcon } from '@/common/icons/general.tsx'
import {
  MarketplaceAffiliation,
  MarketplaceStore,
} from '@/queries/marketplace/schema.ts'
import { useTranslation } from 'react-i18next'
import Image from '../../../common/components/Image/Image'
import Cart from '../components/Cart'
import StoreCard from './StoreCard'
import AffiliationCard from './AffiliationCard'

type MarketplaceView = 'stores' | 'items' | 'affiliations'

interface Props {
  // Data
  stores: MarketplaceStore[]
  affiliations: MarketplaceAffiliation[]
  items: MarketplaceItem[]
  totalRecords: number

  // UI State
  showCart: boolean
  search: string
  currentView: MarketplaceView
  showAffiliationsView?: boolean

  // Pagination
  currentPage: number
  perPage: number

  // Filters
  filters: ListItem[]

  // Actions
  onSearchChange: (search: string) => void
  onViewChange: (view: MarketplaceView) => void
  onFilterChange: (item: ListItem) => void
  onItemClick: (item: MarketplaceItem) => void
  onAffiliationClick: (affiliation: MarketplaceAffiliation) => void
  onProviderClick: (providerId: number) => void
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  hideCart: () => void
  handleStoreClick: (storeId: number) => void
}

export default function MarketplaceUI({
  stores,
  affiliations,
  items,
  totalRecords,
  showCart,
  search,
  currentView,
  currentPage,
  perPage,
  showAffiliationsView = true,

  filters,

  // Actions
  onSearchChange,
  onViewChange,
  onFilterChange,
  onItemClick,
  onAffiliationClick,
  onProviderClick,
  onPageChange,
  onPerPageChange,
  handleStoreClick,
  hideCart,
}: Props) {
  const { t: translate } = useTranslation()

  const isStoresView = currentView === 'stores'
  const isItemsView = currentView === 'items'
  const isAffiliationsView = currentView === 'affiliations'

  return (
    <Card className="overflow-visible relative z-10">
      <CardHeader className="flex justify-between items-center text-md font-semibold">
        <div className="text-left text-md font-semibold text-gray-900">
          {isStoresView
            ? 'Stores'
            : isAffiliationsView
              ? 'Affiliations'
              : translate('items')}
        </div>
        {!showCart && (
          <TableToolBar>
            {showAffiliationsView && (
              <div className="rounded-md shadow-sm lg-sm:flex-none">
                <Button
                  variant={isAffiliationsView ? 'primary' : 'outline'}
                  size="md"
                  onClick={() => onViewChange('affiliations')}
                  className="w-full whitespace-nowrap"
                >
                  Show Affiliations
                </Button>
              </div>
            )}

            <div className="rounded-md shadow-sm lg-sm:flex-none lg-sm:ps-4">
              <Button
                variant={isItemsView ? 'primary' : 'outline'}
                size="md"
                onClick={() => onViewChange('items')}
                className="w-full whitespace-nowrap"
              >
                Show Items
              </Button>
            </div>

            <div className="rounded-md shadow-sm lg-sm:flex-none lg-sm:ps-4">
              <Button
                variant={isStoresView ? 'primary' : 'outline'}
                size="md"
                onClick={() => onViewChange('stores')}
                className="w-full whitespace-nowrap"
              >
                Show Stores
              </Button>
            </div>

            <div className="relative rounded-md shadow-sm flex-1 lg-sm:ps-4">
              <Input
                leftIcon={<MagnifierIcon />}
                className="shadow-none w-full"
                placeholder={
                  isAffiliationsView
                    ? 'Search by Affiliation Name'
                    : 'Search by Name or Type'
                }
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>

            {!isAffiliationsView && (
              <div className="relative rounded-md shadow-sm flex-1">
                <SimpleSelector
                  list={filters}
                  onChange={onFilterChange}
                  className="pt-4 lg-sm:ps-4 lg-sm:pt-0 w-full"
                  icon={<FilterIcon className="text-primary" />}
                />
              </div>
            )}
          </TableToolBar>
        )}
      </CardHeader>

      <CardContent>
        <div className="flex flex-col">
          {showCart ? (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                  <button
                    onClick={hideCart}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <BackIcon />
                  </button>
                  <CartIcon className="w-10 h-10" />
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">Cart</h1>
                  </div>
                </div>
              </div>

              <Cart hideCart={hideCart} />
            </div>
          ) : (
            <>
              {isStoresView ? (
                stores && stores.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6">
                    {stores.map((store, index) => (
                      <StoreCard
                        handleStoreClick={handleStoreClick}
                        key={index}
                        store={store}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-10">
                    No stores were found.
                  </div>
                )
              ) : isAffiliationsView ? (
                affiliations && affiliations.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6">
                    {affiliations.map((affiliation) => (
                      <AffiliationCard
                        key={affiliation.id}
                        affiliation={affiliation}
                        onClick={onAffiliationClick}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-10">
                    No affiliations were found.
                  </div>
                )
              ) : items && items.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6">
                  {items?.map((item) => (
                    <div
                      key={item.id}
                      className="border rounded-lg shadow-lg p-4 flex gap-4"
                    >
                      <div className="w-1/3 flex justify-center">
                        {item.image ? (
                          <Image
                            className="object-contain size-[100px]"
                            path={item.image}
                          />
                        ) : (
                          <div className="h-[100px] w-[100px] bg-gray-200 rounded-md flex items-center justify-center text-sm text-gray-500">
                            No image
                          </div>
                        )}
                      </div>
                      <div className="w-2/3 flex flex-col justify-between">
                        <h2
                          className={cn('text-lg font-semibold leading-tight', {
                            'hover:text-primary-600 cursor-pointer':
                              item.provider.accepting_new_clients === 1,
                          })}
                          onClick={() => {
                            if (item.provider.accepting_new_clients === 1) {
                              onItemClick(item)
                            }
                          }}
                        >
                          {item.name}
                        </h2>
                        <p className="text-sm text-primary">{item.item.name}</p>
                        <p className="text-gray-500">
                          Sold by:{' '}
                          <span
                            className="font-semibold text-primary-600 hover:underline cursor-pointer"
                            onClick={() =>
                              onProviderClick(item?.provider_id ?? 0)
                            }
                          >
                            {item?.provider.name ?? 'Unknown'}
                          </span>
                        </p>
                        <p className="text-xl font-bold text-gray-900 mt-2">
                          {currencyFormatter.format(item.price)}
                        </p>
                        <Button
                          disabled={item.provider.accepting_new_clients === 0}
                          onClick={() => onItemClick(item)}
                          className="w-max py-2 px-4 mt-3"
                        >
                          {translate('shop')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-10">
                  No marketplace items were found.
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-0 border-t border-transparent mt-5">
        {!showCart && (
          <Pagination
            itemsPerPage={perPage}
            currentPage={currentPage}
            totalRows={totalRecords}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        )}
      </CardFooter>
    </Card>
  )
}
